import { noop } from './hooks/usePersistFn';

export interface ThrottleOptions {
  leading?: boolean;
  trailing?: boolean;
}

export default function throttle<T extends noop>(fn: T, wait: number, options: ThrottleOptions = {}) {
  let timer: ReturnType<typeof setTimeout> | null;
  let previousTime = 0;
  let result: ReturnType<T> | undefined;

  const { leading = true, trailing = true } = options;

  const throttled = (...params: Parameters<T>): ReturnType<T> => {
    const currentTime = Date.now();

    if (!previousTime && !leading) {
      previousTime = currentTime;
    }

    const remainingTime = wait - (currentTime - previousTime);

    if (remainingTime <= 0) {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }

      previousTime = currentTime;
      result = fn(...params);

      if (!trailing) {
        previousTime = 0;
      }
    } else if (!timer && trailing) {
      timer = setTimeout(() => {
        previousTime = leading ? Date.now() : 0;
        result = fn(...params);
        timer = null;
      }, remainingTime);
    }

    return result as ReturnType<T>;
  };

  return throttled;
}
